/**
 * First-visit onboarding (FR-1, FR-1b, A4, NFR-8).
 *
 * The acceptance criterion this screen exists to satisfy: **a first-time
 * visitor goes from landing page to a live board in under 60 seconds on a
 * phone, with no install, no account, and no manual data entry.**
 *
 * Consequences that shaped the flow:
 *  - Three inputs, in order, each one auto-advancing: username → league →
 *    draft. Nothing else is required to reach a working board.
 *  - Ranking sources load automatically afterwards. BYOR is never on this
 *    path — every optional step is skippable and still yields a working
 *    board.
 *  - The player-list download (about 14MB, once a day) starts as soon as the
 *    username is submitted, in parallel with league resolution, because it is
 *    the long pole and waiting for it serially would blow the budget on its own.
 */

import { el, clear, text } from './dom.ts';
import type { Store } from '../state/store.ts';
import { getLeagues, getUser, getDrafts, type SleeperDraft, type SleeperLeague } from '../sources/sleeper.ts';
import { loadPlayerUniverse } from '../state/playerCache.ts';
import type { PlayerUniverse } from '../core/types.ts';
import { describeFormat, deriveLeagueConfig } from '../core/scoring.ts';
import type { AccountManager } from '../auth/manager.ts';
import { renderAccountPanel } from './accountPanel.ts';

export function renderOnboarding(root: HTMLElement, store: Store, account?: AccountManager, turnstileSiteKey: string | null = null): void {
  clear(root);

  const state = store.get();
  const status = el('p', { class: 'onboarding-status', role: 'status', 'aria-live': 'polite' });
  const stepHost = el('div', { class: 'onboarding-steps' });

  // The player universe download is kicked off the moment we have a username,
  // and awaited only at the point it is genuinely needed.
  let universePromise: Promise<PlayerUniverse> | null = null;

  let slowTimer: ReturnType<typeof setTimeout> | null = null;
  let accountOpen = false;

  const setStatus = (message: string, kind: 'info' | 'error' = 'info') => {
    if (slowTimer) {
      clearTimeout(slowTimer);
      slowTimer = null;
    }
    clear(status);
    status.className = `onboarding-status ${kind}`;
    status.appendChild(text(message));
  };

  /**
   * Say something after a few seconds of silence.
   *
   * A frozen status line is indistinguishable from a hung page, and the whole
   * point of this screen is that a stranger reaches a working board in under a
   * minute without wondering whether it is broken.
   */
  const setStatusWithSlowHint = (message: string, hint: string, afterMs = 4000) => {
    setStatus(message);
    slowTimer = setTimeout(() => {
      slowTimer = null;
      clear(status);
      status.appendChild(text(`${message} ${hint}`));
    }, afterMs);
  };

  // --- Step 1: username ----------------------------------------------------
  const renderUsernameStep = () => {
    clear(stepHost);
    const input = el('input', {
      type: 'text',
      id: 'sleeper-username',
      class: 'input-lg',
      placeholder: 'e.g. joshdrafts',
      autocomplete: 'username',
      autocapitalize: 'none',
      autocorrect: 'off',
      spellcheck: 'false',
      value: state.config.sleeper_username ?? '',
    }) as HTMLInputElement;

    const submit = async (event: Event) => {
      event.preventDefault();
      const username = input.value.trim();
      if (!username) {
        setStatus('Enter your Sleeper username to get started.', 'error');
        input.focus();
        return;
      }

      setStatusWithSlowHint('Looking up your leagues…', 'Still trying — Sleeper may be slow right now.');
      button.disabled = true;

      try {
        const user = await getUser(username);
        // Start the big download now; league resolution runs alongside it.
        universePromise ??= loadPlayerUniverse({
          onProgress: (stage) => setStatus(stage),
        }).then((r) => r.universe);

        const season = state.config.season;
        const leagues = await getLeagues(user.user_id, season);

        store.patchConfig({ sleeper_username: username, sleeper_user_id: user.user_id });

        if (leagues.length === 0) {
          setStatus(
            `No ${season} NFL leagues found for "${username}". Check the season, or the spelling of your username.`,
            'error',
          );
          button.disabled = false;
          return;
        }
        setStatus(`Found ${leagues.length} league${leagues.length === 1 ? '' : 's'}.`);
        renderLeagueStep(user.user_id, leagues);
      } catch (err) {
        setStatus(
          err instanceof Error && /404/.test(err.message)
            ? `No Sleeper user called "${username}".`
            : 'Could not reach Sleeper. Check your connection and try again.',
          'error',
        );
        button.disabled = false;
      }
    };

    const button = el('button', { class: 'btn btn-primary', type: 'submit' }, 'Find my leagues') as HTMLButtonElement;

    const form = el(
      'form',
      { class: 'onboarding-form', onSubmit: submit },
      el('label', { for: 'sleeper-username' }, 'Your Sleeper username'),
      input,
      button,
      el(
        'p',
        { class: 'hint' },
        'Read-only. Your username, league, and picks are sent from your browser straight to Sleeper — they never touch this site’s server.',
      ),
    );

    stepHost.appendChild(form);
    input.focus();
  };

  // --- Step 2: league ------------------------------------------------------
  const renderLeagueStep = (userId: string, leagues: SleeperLeague[]) => {
    clear(stepHost);

    const list = el('div', { class: 'choice-list' });
    for (const league of leagues) {
      const { config } = deriveLeagueConfig(league, null);
      list.appendChild(
        el(
          'button',
          {
            class: 'choice',
            type: 'button',
            onClick: () => void chooseLeague(userId, league),
          },
          el('span', { class: 'choice-title' }, league.name),
          el('span', { class: 'choice-sub' }, describeFormat(config)),
        ),
      );
    }

    stepHost.appendChild(el('h2', {}, 'Pick your league'));
    stepHost.appendChild(list);
    stepHost.appendChild(
      el('button', { class: 'btn btn-quiet', type: 'button', onClick: renderUsernameStep }, '← Different username'),
    );
  };

  const chooseLeague = async (userId: string, league: SleeperLeague) => {
    setStatusWithSlowHint('Finding drafts…', 'Still trying…');
    try {
      const drafts = await getDrafts(league.league_id);
      if (drafts.length === 0) {
        setStatus(`"${league.name}" has no draft yet. Create one in Sleeper, then reload.`, 'error');
        return;
      }
      if (drafts.length === 1) {
        void start(userId, league, drafts[0]!);
        return;
      }
      renderDraftStep(userId, league, drafts);
    } catch {
      setStatus('Could not load drafts for that league.', 'error');
    }
  };

  // --- Step 3: draft (only when there is more than one) --------------------
  const renderDraftStep = (userId: string, league: SleeperLeague, drafts: SleeperDraft[]) => {
    clear(stepHost);
    const list = el('div', { class: 'choice-list' });

    for (const draft of [...drafts].sort((a, b) => (b.start_time ?? 0) - (a.start_time ?? 0))) {
      const when = draft.start_time ? new Date(draft.start_time).toLocaleString() : 'no start time set';
      list.appendChild(
        el(
          'button',
          { class: 'choice', type: 'button', onClick: () => void start(userId, league, draft) },
          el('span', { class: 'choice-title' }, `${draft.type} · ${draft.status}`),
          el('span', { class: 'choice-sub' }, `${draft.season} · ${when}`),
        ),
      );
    }

    stepHost.appendChild(el('h2', {}, 'Pick your draft'));
    stepHost.appendChild(list);
  };

  const start = async (userId: string, league: SleeperLeague, draft: SleeperDraft) => {
    setStatus('Loading the player list…');
    try {
      universePromise ??= loadPlayerUniverse({ onProgress: setStatus }).then((r) => r.universe);
      const universe = await universePromise;
      await store.initialiseDraft(universe, league.league_id, draft.draft_id, userId);
    } catch (err) {
      setStatus(
        err instanceof Error ? err.message : 'Something went wrong loading the draft.',
        'error',
      );
    }
  };

  const accountHost = el('div', { class: 'onboarding-account-host' });
  const savedProfiles = el('div', { class: 'onboarding-profiles' });
  if (account?.getState().auth.status === 'signed-in' && account.getState().snapshot.profiles.length > 0) {
    savedProfiles.appendChild(el('p', { class: 'hint' }, 'Use a saved Sleeper profile:'));
    for (const profile of account.getState().snapshot.profiles) {
      savedProfiles.appendChild(
        el('button', {
          class: 'choice',
          type: 'button',
          onClick: () => account.selectProfile(profile),
        },
        el('span', { class: 'choice-title' }, profile.sleeper_username),
        el('span', { class: 'choice-sub' }, 'Load saved setup'),
        ),
      );
    }
  }
  const accountButton = el('button', {
    class: 'btn btn-small btn-quiet onboarding-account-button',
    type: 'button',
    onClick: () => {
      if (!account) return;
      accountOpen = !accountOpen;
      clear(accountHost);
      if (accountOpen) renderAccountPanel(accountHost, account, () => { accountOpen = false; clear(accountHost); }, turnstileSiteKey);
    },
  }, account?.getState().auth.status === 'signed-in' ? 'Open account and saved profiles' : 'Sign in to sync rankings and setups');

  root.appendChild(
    el(
      'div',
      { class: 'onboarding' },
      el(
        'h1',
        { class: 'brand' },
        el('img', { class: 'brand-logo', src: '/logo.png', alt: '', width: '240', height: '160' }),
        'Draft Puppy',
      ),
      el(
        'p',
        { class: 'tagline' },
        'Your rankings, your league, live — so you can see who is still on the board and who to take next.',
      ),
      stepHost,
      status,
      savedProfiles.childElementCount ? savedProfiles : null,
      account ? el('div', { class: 'onboarding-account-cta' }, accountButton, accountHost) : null,
      el(
        'details',
        { class: 'onboarding-note' },
        el('summary', {}, 'What this does with your data'),
        el(
          'p',
          {},
          account?.getState().auth.status === 'signed-in'
            ? 'Guest data stays in this browser. Your signed-in account stores only the saved profiles and ranking sources you choose to sync; live rosters and picks remain browser-direct.'
            : 'Guest mode stores settings, rankings, and the cached player list in this browser only. An optional account can remember selected rankings and setups across devices.',
        ),
      ),
    ),
  );

  renderUsernameStep();
}
